// Cliente HTTP da API.
//
// Mesma origem sempre: a sessao e um cookie httpOnly com SameSite=Lax, entao
// nao ha token em JavaScript e nada de CORS. Em desenvolvimento o proxy do
// Vite reproduz isso; em producao quem faz e o proxy reverso.

import type { ApiErrorCode } from "@compila/shared";

export class ApiError extends Error {
  readonly code: ApiErrorCode | "NETWORK_ERROR";
  readonly status: number;
  readonly retryAfterSeconds?: number;

  constructor(
    code: ApiErrorCode | "NETWORK_ERROR",
    message: string,
    status: number,
    retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    if (retryAfterSeconds !== undefined) this.retryAfterSeconds = retryAfterSeconds;
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string; retryAfterSeconds?: number };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      ...init,
      headers: {
        ...(init.body ? { "content-type": "application/json" } : {}),
        ...(init.headers ?? {}),
      },
      // Redundante na mesma origem, mas explicito: a sessao e o cookie.
      credentials: "same-origin",
    });
  } catch {
    throw new ApiError(
      "NETWORK_ERROR",
      "Sem conexao com o servidor. Verifique a rede e tente de novo.",
      0,
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body: unknown = text ? safeJson(text) : null;

  if (!response.ok) {
    const parsed = (body ?? {}) as ErrorBody;
    throw new ApiError(
      (parsed.error?.code as ApiErrorCode) ?? "INTERNAL_ERROR",
      parsed.error?.message ?? `Erro ${response.status}`,
      response.status,
      parsed.error?.retryAfterSeconds,
    );
  }

  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path),

  post: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, {
      method: "POST",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),

  patch: <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),

  delete: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, {
      method: "DELETE",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),

  /** Baixa um arquivo servido pela API (CSV de credenciais). */
  download: async (path: string, filename: string): Promise<void> => {
    const response = await fetch(`/api${path}`, {
      method: "POST",
      headers: { accept: "text/csv", "content-type": "application/json" },
      body: "{}",
      credentials: "same-origin",
    });
    if (!response.ok) throw new ApiError("INTERNAL_ERROR", "Falha no download", response.status);
    triggerDownload(await response.blob(), filename);
  },

  /** Variante com corpo, para a criacao em massa de equipes. */
  downloadWithBody: async (
    path: string,
    body: unknown,
    filename: string,
  ): Promise<void> => {
    const response = await fetch(`/api${path}`, {
      method: "POST",
      headers: { accept: "text/csv", "content-type": "application/json" },
      body: JSON.stringify(body),
      credentials: "same-origin",
    });
    if (!response.ok) {
      const text = await response.text();
      const parsed = (safeJson(text) ?? {}) as ErrorBody;
      throw new ApiError(
        (parsed.error?.code as ApiErrorCode) ?? "INTERNAL_ERROR",
        parsed.error?.message ?? "Falha ao criar equipes",
        response.status,
      );
    }
    triggerDownload(await response.blob(), filename);
  },
};

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
